import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Calendar as CalendarIcon, Users } from 'lucide-react';
import { VenueEvent, venueGamesService } from '@/services/venueGamesService';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface VenueUpcomingEventsProps {
  venueId: string;
}

export const VenueUpcomingEvents = ({ venueId }: VenueUpcomingEventsProps) => {
  const [events, setEvents] = useState<VenueEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, [venueId]);

  const loadEvents = async () => {
    try {
      const upcomingEvents = await venueGamesService.getUpcomingEvents(venueId);
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load upcoming events');
    } finally {
      setLoading(false);
    }
  };

  const formatEventTime = (date: string, time: string) => {
    try {
      const eventDateTime = new Date(`${date}T${time}`);
      return {
        date: format(eventDateTime, 'MMM dd, yyyy'),
        time: format(eventDateTime, 'h:mm a') + ' CST'
      };
    } catch {
      return { date: date, time: time };
    }
  };

  const getPostedByText = (event: VenueEvent) => {
    const role = event.posted_by_role;
    const name = event.posted_by_profile?.first_name || event.posted_by_profile?.username || 'Unknown';
    
    if (role === 'venue') return `Posted by Venue`;
    if (role === 'coach') return `Posted by Coach ${name}`;
    if (role === 'team-owner') return `Posted by Team Owner ${name}`;
    return `Posted by ${name}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading upcoming events...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <CalendarIcon className="w-5 h-5 text-primary" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2 text-white">No Events Scheduled</h3>
            <p className="text-gray-400">
              Events will appear here once they are submitted and approved.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => {
              const { date, time } = formatEventTime(event.event_date, event.event_time);
              return (
                <Card key={event.id} className="border-gray-600 bg-gray-800">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{event.event_title}</h3>
                        <p className="text-sm text-gray-400">{getPostedByText(event)}</p>
                      </div>
                      {event.registration_required && (
                        <Badge variant="secondary" className="bg-primary text-black">
                          Registration Required
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span className="text-sm">{date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm">{time}</span>
                      </div>
                      {event.event_location && (
                        <div className="flex items-center gap-2 text-gray-300">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span className="text-sm">{event.event_location}</span>
                        </div>
                      )}
                    </div>

                    {event.event_description && (
                      <p className="text-sm mb-4 text-gray-300">{event.event_description}</p>
                    )}

                    {event.max_participants && (
                      <div className="flex items-center gap-2 text-gray-300 text-sm">
                        <Users className="w-4 h-4 text-primary" />
                        <span>
                          {event.current_participants}/{event.max_participants} participants
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};